/**
 * Aviso al almacén (permiso logistics.stock_check): cuando los pedidos listos para preparar se llevan el stock
 * restante de un artículo (o más de lo que dice Factusol), salga en la pantalla que salga aparece esta ventana
 * para que almacén confirme las unidades. Una pregunta por artículo, aunque lo pidan varios pedidos:
 *  · «Sí, están»          → todos los pedidos de ese artículo quedan bien (verde si no tienen otro motivo).
 *  · «Sí, y hay más»      → igual, y se apunta cuántas hay para regularizar Factusol (resumen de las 16:05).
 *  · «No hay suficientes» → cuántas hay; se reparten por orden de llegada y los que se quedan sin unidades
 *                           pasan a rojo.
 * «Ahora no» lo pospone 10 min. Los pedidos rosas, cancelados o en espera de pago no se preguntan.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check, X, PackageSearch, Clock, Undo2, Loader2, PackageCheck, PackagePlus, PackageX, ArrowRight, Minus, Plus,
  Mail,
} from 'lucide-react'
import { prefactura, StockCheckItem, StockCountResult, StockDecision } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/components/ui/Toast'

const PERMISSION = 'logistics.stock_check'
const SNOOZE_KEY = 'jvs_stock_check_snooze'
const SNOOZE_MS = 10 * 60 * 1000

/** La pregunta cambia si cambian los pedidos o el stock Factusol: entonces vuelve a salir aunque se pospusiera. */
const keyOf = (i: StockCheckItem) => `${i.art}|${i.stock}|${i.orders.map(o => `${o.order_id}x${o.qty}`).join(',')}`
const fmt = (n: number) => (n % 1 === 0 ? String(n) : n.toFixed(2))
const units = (n: number) => `${fmt(n)} ${n === 1 ? 'unidad' : 'unidades'}`

function readSnooze(): Record<string, number> {
  try {
    const raw = JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}')
    return raw && typeof raw === 'object' ? raw : {}
  } catch {
    return {}
  }
}

function writeSnooze(v: Record<string, number>) {
  try { localStorage.setItem(SNOOZE_KEY, JSON.stringify(v)) } catch { /* sin almacenamiento: solo en memoria */ }
}

/** Dos notas suaves para que se entere aunque esté mirando otra cosa. */
function chime() {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    ;[880, 1320].forEach((f, k) => {
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.type = 'sine'
      o.frequency.value = f
      const t = ctx.currentTime + k * 0.18
      g.gain.setValueAtTime(0.0001, t)
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.02)
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35)
      o.connect(g).connect(ctx.destination)
      o.start(t)
      o.stop(t + 0.4)
    })
    setTimeout(() => ctx.close(), 1000)
  } catch { /* el navegador bloquea el audio hasta el primer clic */ }
}

type Answer = { item: StockCheckItem; decision: StockDecision; real?: number }
type Done = { item: StockCheckItem; decision: StockDecision; result: StockCountResult }

export default function StockCheckPrompt() {
  const { user } = useAuth()
  const enabled = !!user?.permissions?.includes(PERMISSION)
  return enabled ? <StockCheckPromptInner /> : null
}

function StockCheckPromptInner() {
  const qc = useQueryClient()
  const toast = useToast()
  const [snooze, setSnooze] = useState<Record<string, number>>(readSnooze)
  const [now, setNow] = useState(Date.now())
  const [answered, setAnswered] = useState<Set<string>>(new Set())
  const [step, setStep] = useState<'ask' | 'mas' | 'menos'>('ask')
  const [done, setDone] = useState<Done | null>(null)

  const q = useQuery({
    queryKey: ['stock-checks-pending'],
    queryFn: prefactura.stockChecksPending,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    retry: 0,
  })

  // re-evaluar los pospuestos cada 30 s
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  const visible = useMemo(
    () => (q.data || []).filter(i => !answered.has(keyOf(i)) && !(snooze[keyOf(i)] > now)),
    [q.data, answered, snooze, now],
  )

  // aviso sonoro cuando aparece una pregunta nueva
  const seen = useRef<Set<string>>(new Set())
  useEffect(() => {
    const fresh = visible.some(i => !seen.current.has(keyOf(i)))
    visible.forEach(i => seen.current.add(keyOf(i)))
    if (fresh) chime()
  }, [visible])

  // nº de comprobaciones en el título de la pestaña
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\) Stock · /, '')
    document.title = visible.length ? `(${visible.length}) Stock · ${base}` : base
    return () => { document.title = document.title.replace(/^\(\d+\) Stock · /, '') }
  }, [visible.length])

  const refreshAll = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['stock-checks-pending'] })
    qc.invalidateQueries({ queryKey: ['prefactura-status'] })
  }, [qc])

  const answerM = useMutation({
    mutationFn: (v: Answer) => prefactura.stockCheckAnswer(v.item.art, v.decision, v.real),
    onSuccess: (result, v) => {
      setAnswered(prev => new Set(prev).add(keyOf(v.item)))
      setStep('ask')
      setDone({ item: v.item, decision: v.decision, result })
      refreshAll()
    },
    onError: (e: any, v) => {
      toast.error('No se pudo guardar la respuesta', e?.response?.data?.detail)
      // ya no hace falta (facturado, entró stock…): fuera de la lista
      if (e?.response?.status === 409 && !String(e?.response?.data?.detail || '').startsWith('Si ')) {
        setAnswered(prev => new Set(prev).add(keyOf(v.item)))
        setStep('ask')
      }
      refreshAll()
    },
  })
  const undoM = useMutation({
    mutationFn: (d: Done) => prefactura.stockCheckUndo(d.item.art),
    onSuccess: (_r, d) => {
      setAnswered(prev => { const n = new Set(prev); n.delete(keyOf(d.item)); return n })
      setDone(null)
      refreshAll()
    },
    onError: (e: any) => toast.error('No se pudo deshacer', e?.response?.data?.detail),
  })

  const postpone = useCallback(() => {
    const until = Date.now() + SNOOZE_MS
    const next = { ...readSnooze() }
    Object.keys(next).forEach(k => { if (next[k] < Date.now()) delete next[k] })
    visible.forEach(i => { next[keyOf(i)] = until })
    writeSnooze(next)
    setSnooze(next)
    setStep('ask')
  }, [visible])

  const current = visible[0]

  // teclado: S = sí · M = hay más · N = no hay suficientes · Esc = ahora no / volver · Enter = siguiente
  useEffect(() => {
    if (!current && !done) return
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (answerM.isPending || undoM.isPending) return
      if (done) {
        if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setDone(null) }
        return
      }
      if (!current) return
      const k = e.key.toLowerCase()
      if (k === 'escape') { e.preventDefault(); step !== 'ask' ? setStep('ask') : postpone() }
      else if (step === 'ask' && k === 's') { e.preventDefault(); answerM.mutate({ item: current, decision: 'ok' }) }
      else if (step === 'ask' && k === 'm') { e.preventDefault(); setStep('mas') }
      else if (step === 'ask' && k === 'n') { e.preventDefault(); setStep('menos') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [current, done, step, answerM, undoM, postpone])

  if (!current && !done) return null
  const total = visible.length + (done ? 1 : 0)

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-slate-800 shadow-2xl ring-1 ring-black/5 overflow-hidden">
        {/* cabecera */}
        <div className="relative bg-gradient-to-br from-amber-400 via-orange-500 to-orange-600 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30">
              <PackageSearch className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold leading-tight">Comprobación de stock</h2>
              <p className="text-sm text-white/85">
                {current && current.orders.length > 1 && !done
                  ? `${current.orders.length} pedidos se disputan las últimas unidades`
                  : 'Un pedido se lleva el stock restante del almacén'}
              </p>
            </div>
          </div>
          {total > 1 && (
            <span className="absolute right-5 top-5 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-medium ring-1 ring-white/30">
              1 de {total}
            </span>
          )}
        </div>

        {done ? (
          <DoneView
            done={done}
            hasNext={visible.length > 0}
            onNext={() => setDone(null)}
            onUndo={() => undoM.mutate(done)}
            undoing={undoM.isPending}
          />
        ) : current && (
          <div className="px-6 pb-6 pt-5">
            <ItemCard item={current} />

            {step === 'ask' ? (
              <>
                <p className="mt-6 text-center text-lg font-medium text-slate-800 dark:text-slate-100">
                  {current.orders.length > 1
                    ? <>¿Tienes las {fmt(current.demand)} unidades de <span className="font-mono">{current.art}</span> para los {current.orders.length} pedidos?</>
                    : <>¿Tienes {units(current.demand)} de <span className="font-mono">{current.art}</span> en el almacén?</>}
                </p>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <BigButton
                    tone="emerald" icon={<Check className="h-7 w-7" />} label="Sí, están" hint="tecla S"
                    pending={answerM.isPending} onClick={() => answerM.mutate({ item: current, decision: 'ok' })}
                  />
                  <BigButton
                    tone="sky" icon={<PackagePlus className="h-7 w-7" />} label="Sí, y hay más" hint="tecla M"
                    disabled={answerM.isPending} onClick={() => setStep('mas')}
                  />
                  <BigButton
                    tone="rose" icon={<X className="h-7 w-7" />} label="No hay suficientes" hint="tecla N"
                    disabled={answerM.isPending} onClick={() => setStep('menos')}
                  />
                </div>
              </>
            ) : step === 'mas' ? (
              <MoreUnits
                demand={current.demand}
                pending={answerM.isPending}
                onPick={(n) => answerM.mutate({ item: current, decision: 'mas', real: n })}
                onBack={() => setStep('ask')}
              />
            ) : (
              <FewerUnits
                demand={current.demand}
                pending={answerM.isPending}
                onPick={(n) => answerM.mutate({ item: current, decision: 'menos', real: n })}
                onBack={() => setStep('ask')}
              />
            )}

            <div className="mt-5 flex justify-center">
              <button
                onClick={postpone}
                disabled={answerM.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                <Clock className="h-4 w-4" /> Ahora no, recuérdamelo en 10 min
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

function ItemCard({ item }: { item: StockCheckItem }) {
  const multi = item.orders.length > 1
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/40 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{item.art}</p>
          {item.product_name && (
            <p className="mt-1 text-sm leading-snug text-slate-600 dark:text-slate-300">{item.product_name}</p>
          )}
        </div>
        <div className="shrink-0 rounded-xl bg-orange-100 dark:bg-orange-900/40 px-4 py-2 text-center ring-1 ring-orange-200 dark:ring-orange-800">
          <p className="text-3xl font-bold leading-none text-orange-700 dark:text-orange-300">{fmt(item.demand)}</p>
          <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-orange-700/80 dark:text-orange-300/80">
            {item.demand === 1 ? 'unidad' : 'unidades'}
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        {item.orders.map((o, k) => (
          <div key={o.order_id} className="flex items-center gap-2 rounded-lg bg-white dark:bg-slate-800 px-2.5 py-1.5 text-sm ring-1 ring-slate-200 dark:ring-slate-700">
            {multi && (
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-[11px] font-semibold text-slate-500 dark:text-slate-300">
                {k + 1}
              </span>
            )}
            <span className="font-medium text-slate-800 dark:text-slate-100">Pedido {o.order_id}</span>
            {o.customer && <span className="min-w-0 flex-1 truncate text-slate-500 dark:text-slate-400">{o.customer}</span>}
            <span className="ml-auto shrink-0 font-medium text-slate-700 dark:text-slate-200">{units(o.qty)}</span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Factusol dice {units(item.stock)}{multi ? ' · si no hay para todos, se reparten por orden de llegada' : ''}
      </p>
    </div>
  )
}

const TONES = {
  emerald: 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/30',
  sky: 'bg-sky-500 hover:bg-sky-600 shadow-sky-500/30',
  rose: 'bg-rose-500 hover:bg-rose-600 shadow-rose-500/30',
}

function BigButton({ tone, icon, label, hint, onClick, pending = false, disabled = false }: {
  tone: keyof typeof TONES
  icon: React.ReactNode
  label: string
  hint: string
  onClick: () => void
  pending?: boolean
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={pending || disabled}
      className={`group flex flex-col items-center justify-center gap-1.5 rounded-xl px-3 py-4 text-white shadow-sm transition hover:shadow-md disabled:opacity-60 ${TONES[tone]}`}
    >
      <span className="transition group-hover:scale-110">{pending ? <Loader2 className="h-7 w-7 animate-spin" /> : icon}</span>
      <span className="text-center text-sm font-semibold leading-tight">{label}</span>
      <kbd className="text-[10px] font-medium text-white/70">{hint}</kbd>
    </button>
  )
}

function MoreUnits({ demand, pending, onPick, onBack }: {
  demand: number
  pending: boolean
  onPick: (n: number) => void
  onBack: () => void
}) {
  const min = Math.floor(demand) + 1
  const [val, setVal] = useState(String(min))
  const n = Number(val)
  const valid = val !== '' && Number.isFinite(n) && n > demand
  const set = (x: number) => setVal(String(Math.max(min, x)))

  return (
    <div className="mt-6">
      <p className="text-center text-lg font-medium text-slate-800 dark:text-slate-100">¿Cuántas unidades hay en total?</p>
      <p className="mt-1 text-center text-sm text-slate-500 dark:text-slate-400">Los pedidos quedan servidos y administración regulariza Factusol después de facturar</p>
      <div className="mt-4 flex items-center justify-center gap-2">
        <button onClick={() => set(n - 1)} disabled={pending || n <= min}
          className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40">
          <Minus className="h-5 w-5" />
        </button>
        <input
          type="number" min={min} value={val} autoFocus
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && valid) onPick(n); if (e.key === 'Escape') onBack() }}
          className="h-14 w-24 rounded-xl border border-sky-300 dark:border-sky-700 bg-white dark:bg-slate-700 text-center text-3xl font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-400"
        />
        <button onClick={() => set((Number.isFinite(n) ? n : min - 1) + 1)} disabled={pending}
          className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
          <Plus className="h-5 w-5" />
        </button>
        <button
          onClick={() => onPick(n)} disabled={!valid || pending}
          className="ml-2 inline-flex h-12 items-center gap-1.5 rounded-xl bg-sky-500 px-4 font-semibold text-white hover:bg-sky-600 disabled:opacity-50"
        >
          {pending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />} Confirmar
        </button>
      </div>
      <div className="mt-3 flex justify-center">
        <button onClick={onBack} disabled={pending} className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">← Volver</button>
      </div>
    </div>
  )
}

function FewerUnits({ demand, pending, onPick, onBack }: {
  demand: number
  pending: boolean
  onPick: (n: number) => void
  onBack: () => void
}) {
  const max = Math.ceil(demand) - 1
  const many = max > 7
  const [val, setVal] = useState('0')
  const n = Number(val)
  const valid = val !== '' && Number.isFinite(n) && n >= 0 && n < demand

  return (
    <div className="mt-6">
      <p className="text-center text-lg font-medium text-slate-800 dark:text-slate-100">¿Cuántas unidades hay realmente?</p>
      <p className="mt-1 text-center text-sm text-slate-500 dark:text-slate-400">Se reparten por orden de llegada; los pedidos sin unidades pasan a rojo</p>
      {many ? (
        <div className="mt-4 flex items-center justify-center gap-2">
          <input
            type="number" min={0} max={max} value={val} autoFocus
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && valid) onPick(n); if (e.key === 'Escape') onBack() }}
            className="h-14 w-24 rounded-xl border border-rose-300 dark:border-rose-800 bg-white dark:bg-slate-700 text-center text-3xl font-bold"
          />
          <button
            onClick={() => onPick(n)} disabled={!valid || pending}
            className="inline-flex h-12 items-center gap-1.5 rounded-xl bg-rose-500 px-4 font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
          >
            {pending ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />} Confirmar
          </button>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {Array.from({ length: max + 1 }, (_, k) => k).map(k => (
            <button
              key={k}
              onClick={() => onPick(k)}
              disabled={pending}
              className="h-14 min-w-14 rounded-xl border-2 border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-900/20 px-4 text-2xl font-bold text-rose-700 dark:text-rose-300 transition hover:border-rose-400 hover:bg-rose-100 disabled:opacity-50"
            >
              {k}
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 flex justify-center">
        <button onClick={onBack} disabled={pending} className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">← Volver</button>
      </div>
    </div>
  )
}

function DoneView({ done, hasNext, onNext, onUndo, undoing }: {
  done: Done
  hasNext: boolean
  onNext: () => void
  onUndo: () => void
  undoing: boolean
}) {
  const { item, result } = done
  const qty = new Map(item.orders.map(o => [o.order_id, o.qty]))
  const got = result.assigned
  const missing = result.missing
  const descuadre = result.real_units !== result.factusol_stock
  const allOk = missing.length === 0

  return (
    <div className="px-6 pb-6 pt-6 text-center">
      <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${allOk ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300' : got.length ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300'}`}>
        {allOk ? <PackageCheck className="h-8 w-8" /> : got.length ? <PackageSearch className="h-8 w-8" /> : <PackageX className="h-8 w-8" />}
      </div>

      {got.length > 0 ? (
        <>
          <p className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
            Aparta {got.length === 1 ? 'la pieza' : 'las piezas'} y márcalas con su pedido
          </p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300"><span className="font-mono font-medium">{item.art}</span></p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {got.map(oid => (
              <span key={oid} className="inline-flex items-baseline gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white dark:bg-white dark:text-slate-900">
                <span className="font-mono text-2xl font-bold tracking-wider">{oid}</span>
                <span className="text-xs opacity-75">{units(qty.get(oid) || 1)}</span>
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">Avisado a administración</p>
      )}

      {missing.length > 0 && (
        <div className="mx-auto mt-4 max-w-sm rounded-xl bg-rose-50 dark:bg-rose-900/20 px-4 py-3 text-sm text-rose-800 dark:text-rose-300 ring-1 ring-rose-200 dark:ring-rose-900">
          <p className="font-medium">Sin unidades (pasan a rojo):</p>
          <p className="mt-0.5 font-mono">{missing.join(' · ')}</p>
        </div>
      )}

      {descuadre && (
        <p className="mx-auto mt-4 flex max-w-sm items-center justify-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <Mail className="h-3.5 w-3.5 shrink-0" />
          Factusol dice {fmt(result.factusol_stock)} y hay {fmt(result.real_units)}: administración lo regulariza (resumen de las 16:05)
        </p>
      )}

      <div className="mt-6 flex items-center justify-center gap-2">
        <button
          onClick={onUndo}
          disabled={undoing}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          {undoing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} Deshacer
        </button>
        <button
          onClick={onNext}
          autoFocus
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
        >
          {hasNext ? <>Siguiente <ArrowRight className="h-4 w-4" /></> : 'Hecho'}
        </button>
      </div>
    </div>
  )
}
